import { BookingStatus, BookingStatusLabel, IBooking } from "@/lib/types";

// ---------------------- Date / Time

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthsShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getWeekDates = () => {
  const now = new Date();
  const todayDayNumber = getToday(); // returns 0-6 (Mon-Sun)

  const monday = new Date(now);
  monday.setDate(now.getDate() - todayDayNumber); // subtract the day number to get to Monday

  const weekDates = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);

    date.setDate(monday.getDate() + i);

    weekDates.push({
      dayOfMonth: date.getDate(),
      dayOfWeek: i,
      fullDate: date,
    });
  }

  return weekDates;
};

const getOffsetWeekDates = (weekOffset: number) => {
  const now = new Date();
  const todayDayNumber = getToday();

  // Get Monday of the target week
  const monday = new Date(now);
  monday.setDate(now.getDate() - todayDayNumber + weekOffset * 7);

  const weekDates = [];

  // Create date range for the week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);

    date.setDate(monday.getDate() + i);
    weekDates.push(date);
  }

  return weekDates;
};

export const filterDatesBySelection = (
  dates: ReturnType<typeof getCalendarDates>,
  selectedFilter: string,
) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  switch (selectedFilter) {
    case "last-week": {
      const weekDates = getOffsetWeekDates(-1);
      return dates.filter((date) =>
        weekDates.some(
          (weekDate) =>
            weekDate.getDate() === date.fullDate.getDate() &&
            weekDate.getMonth() === date.fullDate.getMonth() &&
            weekDate.getFullYear() === date.fullDate.getFullYear(),
        ),
      );
    }
    case "this-week": {
      const weekDates = getOffsetWeekDates(0);
      return dates.filter((date) =>
        weekDates.some(
          (weekDate) =>
            weekDate.getDate() === date.fullDate.getDate() &&
            weekDate.getMonth() === date.fullDate.getMonth() &&
            weekDate.getFullYear() === date.fullDate.getFullYear(),
        ),
      );
    }
    case "next-week": {
      const weekDates = getOffsetWeekDates(1);
      return dates.filter((date) =>
        weekDates.some(
          (weekDate) =>
            weekDate.getDate() === date.fullDate.getDate() &&
            weekDate.getMonth() === date.fullDate.getMonth() &&
            weekDate.getFullYear() === date.fullDate.getFullYear(),
        ),
      );
    }
    case "last-month": {
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const year = currentMonth === 0 ? currentYear - 1 : currentYear;
      return dates.filter(
        (date) => date.fullDate.getMonth() === lastMonth && date.fullDate.getFullYear() === year,
      );
    }
    case "this-month":
      return dates.filter(
        (date) =>
          date.fullDate.getMonth() === currentMonth && date.fullDate.getFullYear() === currentYear,
      );
    case "next-month": {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const year = currentMonth === 11 ? currentYear + 1 : currentYear;
      return dates.filter(
        (date) => date.fullDate.getMonth() === nextMonth && date.fullDate.getFullYear() === year,
      );
    }
    case "all":
    default:
      return dates;
  }
};

export const getCalendarDates = () => {
  const now = new Date();
  const calendarDates = [];

  const startDate = new Date(now);
  startDate.setDate(now.getDate() - 30);

  for (let i = 0; i < 61; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);

    calendarDates.push({
      dayOfMonth: date.getDate(),
      dayOfWeek: (date.getDay() + 6) % 7,
      month: months[date.getMonth()],
      monthShort: monthsShort[date.getMonth()],
      fullDate: date,
    });
  }

  return calendarDates;
};

export const getToday = (): number => {
  const now = new Date();
  const currentDay = now.getDay();

  return currentDay === 0 ? 6 : currentDay - 1;
};

export const getDayName = (day: number) => {
  if (day >= 0 && day < daysOfWeek.length) {
    return daysOfWeek[day];
  } else {
    return "";
  }
};

export const getFormattedTime = (date: string | Date | null | undefined) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return "";
  }

  return dateObj
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\s/, " ");
};

export const getFormattedDate = () => {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const getFormattedTimeAndDate = (input: string | Date | null | undefined) => {
  if (!input) {
    return "";
  }

  const date = input instanceof Date ? input : new Date(input);

  if (isNaN(date.getTime())) {
    return "";
  }

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const dateStr = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return `${timeStr} - ${dateStr}`;
};

export const getShortDate = (date: Date) => {
  return date.toString().split("T")[0];
};

export const isBookingNow = (
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
): boolean => {
  // If either time is missing, booking is not now
  if (!startTime || !endTime) {
    return false;
  }

  // Convert string dates to Date objects if needed
  const startDate = startTime instanceof Date ? startTime : new Date(startTime);
  const endDate = endTime instanceof Date ? endTime : new Date(endTime);

  // Check if dates are valid
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return false;
  }

  const currentDate = new Date();

  // Check if current time falls between start and end times
  return currentDate >= startDate && currentDate <= endDate;
};

// ---------------------- Bookings

export const anyBookingCheckedIn = (bookings: IBooking[]) => {
  return (
    bookings
      .map((booking) => getBookingStatus(booking))
      .filter((status) => status === BookingStatusLabel.CheckedIn).length > 0
  );
};

export const anyBookingLateOrMissed = (bookings: IBooking[]) => {
  return (
    bookings.map((booking) => isPastBookingComplete(booking)).filter((status) => status === false)
      .length > 0
  );
};

export const getBookingStatus = (booking: IBooking): BookingStatusLabel | null => {
  const now = new Date();
  const startTime = new Date(booking.localStartTime);
  const endTime = new Date(booking.localEndTime);

  if (booking.bookingStatus?.toLowerCase() === BookingStatus.InProgress) {
    return BookingStatusLabel.CheckedIn;
  } else {
    if (booking.bookingStatus?.toLowerCase() === BookingStatus.Scheduled) {
      if (endTime.getTime() < now.getTime()) {
        return BookingStatusLabel.Missed;
      } else {
        if (startTime.getTime() < now.getTime()) {
          return BookingStatusLabel.Late;
        }
      }
    }
  }

  return null;
};

export function getStatusText(status: string | null): string {
  if (!status) return "Unknown";

  const lowerStatus = status.toLowerCase();

  switch (lowerStatus) {
    case BookingStatus.Scheduled.toLowerCase():
      return "Scheduled";
    case BookingStatus.InProgress.toLowerCase():
      return "In Progress";
    case BookingStatus.OnBreak.toLowerCase():
      return "On Break";
    default:
      return "Unknown";
  }
}

export const getTodaysBookings = (date: Date, bookings: IBooking[]) => {
  const dayBookings = bookings.filter((booking) => {
    const bookingDate = new Date(booking.localStartTime);

    return (
      bookingDate.getDate() === date.getDate() &&
      bookingDate.getMonth() === date.getMonth() &&
      bookingDate.getFullYear() === date.getFullYear()
    );
  });

  return dayBookings;
};

export const isBookingInProgress = (booking: IBooking): boolean => {
  return ![BookingStatus.Completed, BookingStatus.FollowUp, BookingStatus.Scheduled].includes(
    booking.bookingStatus as BookingStatus,
  );
};

export const isPastBookingComplete = (booking: IBooking): boolean => {
  const now = new Date();
  const bookingTime = new Date(booking.localStartTime);

  // If booking is in the future (even by minutes), return true
  if (bookingTime > now) {
    return true;
  }

  // For bookings in the past or current (including same day/hour/minute),
  // check if status is Completed or FollowUp
  return [
    BookingStatus.Completed,
    BookingStatus.FollowUp,
    BookingStatus.InProgress,
    BookingStatus.OnBreak,
  ].includes(booking.bookingStatus as BookingStatus);
};

// ---------------------- Geolocation

export const getDeviceType = (name: string) => {
  return name === "Desktop" ? 690970000 : 690970001;
};

// ---------------------- Names

export function getInitials(fullName: string | null): string {
  if (fullName === null) return "";

  if (!fullName.trim()) return "";

  const words = fullName
    .trim()
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.split("-")[0]); // Take only first part of hyphenated words

  // Handle invalid cases
  if (words.length < 2 || words.length > 3) return "";

  // For 3 words, use first and last word
  if (words.length === 3) {
    return (words[0].charAt(0) + words[2].charAt(0)).toUpperCase();
  }

  // For 2 words, use both
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}
